/**
 * The process, written for someone who has never seen it.
 *
 * The dashboard names each screen after the artifact it shows — "señales", "prenotas",
 * "enriquecimiento" — which is precise and means nothing to a person opening it for the first
 * time. The feedback was literal: *no entiendo qué es lo que se está describiendo*. So every stage
 * here answers the same five questions, in the same order, in plain language:
 *
 *   qué recibe · qué hace · qué entrega · qué cuesta · qué puede salir mal
 *
 * The copy lives in one file rather than in the diagram so it stays reviewable as text: it is
 * documentation that happens to render, and it is the only place the vocabulary is defined.
 */

import type { EditorialStreamSummary } from 'src/types/editorial';

export type FlowStageId =
  | 'captura'
  | 'transcript'
  | 'topics'
  | 'claims'
  | 'prenota'
  | 'x_review'
  | 'verification'
  | 'enrichment'
  | 'draft'
  | 'review';

export type FlowStage = {
  id: FlowStageId;
  /** Short label for the node. */
  label: string;
  /** One line under the label — what this box is, in six words. */
  tagline: string;
  receives: string;
  does: string;
  delivers: string;
  /** Empty when the stage runs on the box and costs nothing beyond electricity. */
  cost: string;
  /** The failure an editor will actually meet, and how it looks when it happens. */
  failure: string;
  /** Route inside a stream where the artifact can be read, when there is one. */
  to?: string;
  /** Which job stage runs this box, when it maps to one. */
  jobStage?: 'transcript' | 'topics' | 'claims' | 'prenota' | 'x_review' | 'topic_link' | 'notas';
};

export const FLOW_STAGES: FlowStage[] = [
  {
    id: 'captura',
    label: 'Captura',
    tagline: 'Escuchar la transmisión',
    receives: 'La liga del canal, o la de un video que alguien pidió procesar.',
    does: 'Baja el audio en tramos de un minuto mientras la conferencia sigue al aire, sin esperar a que termine.',
    delivers: 'Audio en pedazos, listos para transcribir uno por uno.',
    cost: '',
    failure: 'Si YouTube cambia el formato del stream, no hay audio y no hay nada más. Se ve como una transmisión sin guion.',
  },
  {
    id: 'transcript',
    label: 'Transcripción',
    tagline: 'Pasar el audio a texto',
    receives: 'Los tramos de audio que la captura va dejando, en orden.',
    does: 'Whisper convierte cada tramo en texto y compara cada voz con las voces conocidas para ponerle nombre a quien habla. Una voz que no reconoce queda guardada como voz nueva, con su audio, para que alguien la nombre después.',
    delivers: 'El guion de la conferencia, con quién dijo cada cosa y en qué minuto.',
    cost: '',
    failure: 'Si nadie ha entrenado las voces, todo queda como "Voz 1", "Voz 2". El texto sirve igual; lo que falta es saber de quién es cada frase.',
    to: 'transcript',
    jobStage: 'transcript',
  },
  {
    id: 'topics',
    label: 'Señales',
    tagline: 'De qué se está hablando',
    receives: 'El guion, cortado en tramos de un par de minutos.',
    does: 'Un modelo lee cada tramo y anota de qué trata, qué instituciones y personas aparecen, y qué afirmaciones concretas se hicieron — todavía en las palabras exactas de quien habló.',
    delivers: 'Los temas de la conferencia y una lista cruda de afirmaciones, cada una pegada al minuto donde se dijo.',
    cost: 'Una llamada al modelo rápido por cada tramo.',
    failure: 'Si el proveedor del modelo no responde, el tramo queda vacío — y un tramo vacío se ve igual que un tramo donde no se dijo nada relevante. Por eso ahora una corrida con la mayoría de los tramos fallidos se marca como fallida en vez de reportar éxito.',
    to: 'topics',
    jobStage: 'topics',
  },
  {
    id: 'claims',
    label: 'Afirmaciones',
    tagline: 'Dejar cada dicho en limpio',
    receives: 'Las afirmaciones crudas de cada tramo.',
    does: 'Reescribe cada una en una frase completa que se entienda sola, sin cambiar lo que se dijo: quién lo afirma, si lo afirma o lo niega, de qué periodo habla, qué cifras da. Guarda junto a ella el extracto textual que la respalda.',
    delivers: 'Afirmaciones normalizadas, cada una con su evidencia y un identificador que la sigue hasta la nota publicada.',
    cost: 'Una llamada al modelo rápido por lote de afirmaciones.',
    failure: 'Si una afirmación no comparte vocabulario con su extracto, pierde su respaldo y se marca — antes eso tumbaba la historia entera, ahora sólo esa afirmación.',
    to: 'claims',
    jobStage: 'claims',
  },
  {
    id: 'prenota',
    label: 'Prenotas',
    tagline: 'Agrupar los dichos en historias',
    receives: 'Las afirmaciones normalizadas de toda la conferencia.',
    does: 'Junta las que hablan de lo mismo en una historia: el tema del que trata, quiénes participan, qué se afirmó y con qué evidencia. Decide también qué formatos aguanta esa historia — una nota larga necesita más material que un tuit.',
    delivers: 'Una prenota por historia. No es la nota: es el expediente con el que se escribe.',
    cost: 'Una llamada al modelo de razonamiento por bloque de la conferencia.',
    failure: 'Una historia sin ninguna afirmación respaldada se bloquea, y es correcto: no hay con qué escribirla.',
    to: 'stories',
    jobStage: 'prenota',
  },
  {
    id: 'x_review',
    label: 'Relevancia',
    tagline: '¿Le importa a alguien?',
    receives: 'Cada historia armada en la etapa anterior, con sus afirmaciones.',
    does: 'Busca en la conversación pública de X qué tanto se está hablando de eso, con qué intensidad y desde qué posturas, y le pone una calificación de 0 a 100 con seis criterios explícitos.',
    delivers: 'Una calificación por historia, con el desglose de cómo se llegó a ella.',
    cost: 'Servicio de pago. Alrededor de dos dólares por conferencia.',
    failure: 'Si esta etapa no corre, las historias se quedan sin calificación y la redacción no las toma: un puntaje desconocido no pasa la compuerta.',
    to: 'relevance',
    jobStage: 'x_review',
  },
  {
    id: 'verification',
    label: 'Verificación',
    tagline: 'Contrastar contra el mundo',
    receives: 'Las historias que pasaron la compuerta de relevancia.',
    does: 'Busca fuentes externas para cada afirmación y emite un veredicto: sustentada, parcialmente sustentada, contradicha o no verificable. Guarda las fuentes.',
    delivers: 'Veredictos con fuentes citables, y los datos externos que la conferencia no dio.',
    cost: 'Servicio de pago: búsquedas web y un modelo de razonamiento por historia.',
    failure: 'Si falla, la nota se escribe igual pero sólo con lo dicho en la conferencia: más corta y sin fuentes externas.',
    to: 'verification',
    jobStage: 'notas',
  },
  {
    id: 'enrichment',
    label: 'Enriquecimiento',
    tagline: 'Decidir qué entra en la nota',
    receives: 'La historia verificada y el perfil editorial con el que se va a escribir.',
    does: 'Arma el plan de la pieza para ese perfil: qué secciones tiene, en qué orden, qué dato sostiene cada una. No agrega hechos — sólo decide cuáles usar y cómo ordenarlos.',
    delivers: 'El plan aprobado que el redactor debe seguir, y el formato que el material aguanta.',
    cost: 'Una llamada al modelo de razonamiento por perfil.',
    failure: 'Si el material no alcanza para el formato pedido, baja al siguiente: una historia que no da para nota larga sí da para una nota breve.',
    jobStage: 'notas',
  },
  {
    id: 'draft',
    label: 'Redacción',
    tagline: 'Escribir la nota, tres veces',
    receives: 'El plan y los hechos con su evidencia.',
    does: 'Escribe la pieza en tres voces editoriales — izquierda, centro y derecha — con los mismos hechos y distinto encuadre. Después un validador compara cada párrafo contra la evidencia y rechaza lo que no esté sostenido; si hay reparaciones, reescribe.',
    delivers: 'Tres notas por historia, cada párrafo citando la afirmación que lo respalda.',
    cost: 'Varias llamadas al modelo por perfil. Por eso se escribe primero una y sólo si esa pasa se escriben las otras dos.',
    failure: 'Un borrador que inventa un dato no se publica: el validador lo bloquea y la nota queda marcada para revisión humana.',
    to: 'notes',
    jobStage: 'notas',
  },
  {
    id: 'review',
    label: 'Revisión',
    tagline: 'La última palabra es humana',
    receives: 'Las notas redactadas.',
    does: 'Un editor aprueba, edita o rechaza, y dice por qué. Esa decisión se guarda.',
    delivers: 'La nota que se publica — y la enseñanza: lo que se aprueba sin tocar se le muestra al redactor como referencia de voz, y lo que se rechaza mueve los umbrales de la etapa culpable.',
    cost: '',
    failure: 'Nada se publica solo. Una nota sin revisar se queda esperando.',
    to: 'notes',
  },
];

/** Which stages a job actually pays for, in the order the queue runs them. */
export const PAID_FLOW_STAGES: ReadonlySet<FlowStageId> = new Set<FlowStageId>([
  'x_review',
  'verification',
  'enrichment',
  'draft',
]);

export type FlowStanding = 'done' | 'partial' | 'skipped' | 'not_reached' | 'unknown';

/**
 * What this broadcast actually produced at each box.
 *
 * Read from the counts the API computes, not from a status field: a stream processed before the
 * job queue existed has no job to ask, and its artifacts are the only honest answer either way.
 */
export function flowStandings(summary: EditorialStreamSummary): Record<FlowStageId, { standing: FlowStanding; detail: string }> {
  const state = (count: number, unit: string, zero: string): { standing: FlowStanding; detail: string } =>
    count > 0 ? { standing: 'done', detail: `${count} ${unit}` } : { standing: 'not_reached', detail: zero };

  return {
    captura: { standing: 'done', detail: '1 transmisión' },
    transcript: summary.turns > 0 ? { standing: 'done', detail: `${summary.turns} intervenciones` } : { standing: 'not_reached', detail: 'sin guion' },
    topics: state(summary.signals, 'tramos', 'no se ejecutó'),
    claims: state(summary.claims, 'afirmaciones', 'no se ejecutó'),
    prenota: state(summary.prenotas, 'historias', 'no se ejecutó'),
    x_review: state(summary.xReviews, 'calificadas', 'no se ejecutó'),
    // Partial is the honest reading when only some stories cleared the relevance gate.
    verification:
      summary.verifications === 0
        ? { standing: 'skipped', detail: 'no se ejecutó' }
        : {
            standing: summary.verifications < summary.prenotas ? 'partial' : 'done',
            detail: `${summary.verifications} de ${summary.prenotas} historias`,
          },
    enrichment: state(summary.outputs, 'planes', 'no se ejecutó'),
    draft:
      summary.outputs === 0
        ? { standing: 'not_reached', detail: 'sin borradores' }
        : { standing: 'done', detail: `${summary.outputs} borradores · ${summary.profiles} perfiles` },
    review: state(summary.reviews, 'revisiones', 'sin revisar'),
  };
}
